import { DatePicker } from "@rio-cloud/rio-uikit"
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";

interface Props {
  startDate: string;
  endDate: string;
  handleStartDate: (date: string) => void;
  handleEndDate: (date: string) => void;
}

const DatePickerFilter = ({endDate, handleEndDate, handleStartDate, startDate}: Props) => {
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [hasEndDatePickerError, setHasEndDatePickerError] = useState(false);

  const formatDateAsTyping = (value: string) => {
    const onlyDigits = value.replace(/\D/g, "").slice(0, 8);

    if (onlyDigits.length <= 2) return onlyDigits;
    if (onlyDigits.length <= 4) {
      return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2)}`;
    }

    return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2, 4)}/${onlyDigits.slice(4)}`;
  };

  const formatDateForDisplay = (date?: string) => {
    if (!date) return "";
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  const parseTypedDate = (value: string) => {
    if (value.length !== 10) return null;

    const parsedDate = moment(value, "DD/MM/YYYY", true);
    return parsedDate.isValid() ? parsedDate : null;
  };

  useEffect(() => {
    setStartInputValue(formatDateForDisplay(startDate));
    setEndInputValue(formatDateForDisplay(endDate));
  }, [startDate, endDate]);


  const getStartDate = (result: Moment | string) => {
    if (typeof result === "string") {
      const formattedInput = formatDateAsTyping(result);

      setStartInputValue(formattedInput);

      if (!formattedInput) {
        setEndInputValue("");
        return;
      }

      const typedDate = parseTypedDate(formattedInput);

      if (!typedDate) return;

      const start = typedDate.clone();
      const startFormatted = start.format("YYYY-MM-DD");
      const endOfMonth = start.clone().endOf("month").format("YYYY-MM-DD");

      setEndInputValue(start.clone().endOf("month").format("DD/MM/YYYY"));
      handleStartDate(startFormatted)
      handleEndDate(endOfMonth)

      return;
    }

    if (!result.isValid()) return;

    const start = result.clone();
    const startFormatted = start.format("YYYY-MM-DD");

    const endOfMonth = start.clone().endOf("month").format("YYYY-MM-DD");

    setStartInputValue(start.format("DD/MM/YYYY"));
    setEndInputValue(start.clone().endOf("month").format("DD/MM/YYYY"));
    handleStartDate(startFormatted)
    handleEndDate(endOfMonth)
  };

  const getEndDateValidation = (result: Moment | string) => {
    if (typeof result === "string") {
      const formattedInput = formatDateAsTyping(result);
      setEndInputValue(formattedInput);

      if (!formattedInput) {
        return;
      }

      const typedDate = parseTypedDate(formattedInput);
      if (!typedDate) return;

      const date = typedDate.format("YYYY-MM-DD");

      if (startDate && typedDate.isBefore(moment(startDate, "YYYY-MM-DD"), "day")) {
        setEndInputValue("");
        setHasEndDatePickerError(true)
        return
      }

      if (!isRangeDataValid(typedDate)) {
        setEndInputValue("");
        setHasEndDatePickerError(true)
        return
      }

      setHasEndDatePickerError(false)
      handleEndDate?.(date);
      return;
    }

    if (!result.isValid()) return;

    const selectedDate = result.clone();
    const date = selectedDate.format("YYYY-MM-DD");

    if (startDate && selectedDate.isBefore(moment(startDate, "YYYY-MM-DD"), "day")) {
      setEndInputValue("");
      setHasEndDatePickerError(true)
      return
    }

    if (!isRangeDataValid(selectedDate)) {
      setEndInputValue("");
      setHasEndDatePickerError(true)
      return
    }

    setEndInputValue(selectedDate.format("DD/MM/YYYY"));
    handleEndDate?.(date);
    setHasEndDatePickerError(false)
  };

  const isRangeDataValid = (selectedDate: Moment) => {
    if (selectedDate && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(5, "year");

      return selectedDate.isSameOrAfter(minDate, "day") && selectedDate.isSameOrBefore(maxDate, "day");
    }

    return {};
  };

  const getValidDateProps = (isEndDatePicker: boolean) => {
    if (isEndDatePicker && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(5, "year");
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    return {};
  };


  return (
    <>
      <div className='width-150'>
          <DatePicker
            timeFormat={false}
            id='start-date-picker'
            dateFormat='DD/MM/YYYY'
            className='rounded-small'
            inputProps={{ placeholder: "DD/MM/AAAA" }}
            onChange={getStartDate}
            value={startInputValue}
            locale="pt-Br"
          />
      </div>
      <div>
          <span className='text-normal text-size-14 text-color-darkest'>até</span>
      </div>
      <div className='width-150'>
        <div className={`form-group margin-0 ${hasEndDatePickerError ? 'has-feedback has-error' : ''}`}>
          <DatePicker
            id='end-date-picker'
            className='rounded-small'
            dateFormat='DD/MM/YYYY'
            timeFormat={false}
            value={endInputValue}
            onChange={getEndDateValidation}
            locale="pt-Br"
            {...getValidDateProps(true)}
          />
        </div>
      </div>
    </>
  )
}

export default DatePickerFilter
